import { FC, useCallback } from "react";
import { routePaths } from "../../../Router/router";
import { Grid2, Link, MenuItem, Select, SelectChangeEvent, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";
import { GridActionsCellItem, GridColDef, useGridApiRef, GridRowParams } from "@mui/x-data-grid-premium";
import CachedIcon from "@mui/icons-material/Cached";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import React from "react";
import PageFilter from "../../Misc/PageFilter";
import { cockpitService, jobBrokerService, jobsNotifier } from "../../../Core/Api/services";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import DataTable, { CellSize } from "../table";
import { ModalContext } from "../../Modals";
import { produce } from "immer";
import { EventNotifierOperation } from "../../../Core/Api/notifier-client";
import TableCellDateTime from "../TableCellDateTime/table-cell-date-time";
import TableCellIngestProgressBar from "../TableCellIngestProgressBar/table-cell-ingest-progress-bar";
import ListIcon from "@mui/icons-material/List";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { JobTypes } from "../../../Core/Api/Types/job-types";
import { JobStatus } from "../../../Core/Api/Types/job-status";
import { DbJobInfos } from "../../../Core/Api/Types/db-job-infos";
import ModalJobDetails, { ModalJobDetailsElementProps } from "../../Modals/ModalShowDetails/modal-job-details";
import ModalJobRetry, { ModalJobRetryElementProps } from "../../Modals/ModalJobRetry/modal-job-retry";
import TableCellJobStatus from "../TableCellJobStatus/table-cell-job-status";
import TableCellJobPriority from "../TableCellJobPriority/table-cell-job-priority";
import OrderItemPanel from "../../Panels/OrderItemPanel/order-item-panel";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import { PlatformNames } from "../../../Core/Api/Types/platform-names";
import { MakeOperationRequest } from "../../../Core/Api/Types/make-operation-request";
import { ResourceType } from "../../../Core/Api/Types/cockpit-ressource-type";
import { Action } from "../../../Core/Api/Types/cockpit-action";
import PlaceIcon from "@mui/icons-material/Place";
import { useTranslation } from "react-i18next";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { WorkspacesOutlined } from "@mui/icons-material";
import TableUuids from "../table-uuids";
import { GetEnumValueLabelMap } from "../../../Utils/helpers";

interface JobsTableProps {
  jobs: DbJobInfos[];
  platforms?: PlatformNames[];
  isCockpit?: boolean;
  onFilteredItemCountChange?: (count: number) => void;
  isLoading?: boolean;
}

const JobsTable: FC<JobsTableProps> = (props) => {
  const { t } = useTranslation();
  const { jobs, platforms, isCockpit = false, onFilteredItemCountChange, isLoading } = props;

  const [filterType, setFilterType] = React.useState<JobTypes | "">("");
  const [filterStatus, setFilterStatus] = React.useState<JobStatus | undefined>(undefined);
  const [filterPlatform, setFilterPlatform] = React.useState<number>(-1);
  const [filterPriority, setFilterPriority] = React.useState<number>(-1);

  // Order panel
  const [panelOrderItemOpen, setPanelOrderItemOpen] = React.useState<boolean>(false);
  const [selectedOrderItemId, setSelectedOrderItemId] = React.useState<number>(-1);

  // Table rows
  const apiGrid = useGridApiRef();
  const [rows, setRows] = React.useState<DbJobInfos[]>([]);

  // Modal
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  // Box filters
  const enqueueCustomSnackbar = useCustomSnackbar();

  const handleFilterPlatform = (event: SelectChangeEvent<number>) => {
    setFilterPlatform(event.target.value as number);
  };
  const handleFilterPriority = (event: SelectChangeEvent<number>) => {
    setFilterPriority(event.target.value as number);
  };
  const changeStatusFilter = useCallback(
    (status: JobStatus | undefined) => {
      if (filterStatus === status) setFilterStatus(undefined);
      else setFilterStatus(status);
    },
    [filterStatus]
  );

  // SignalR
  React.useEffect(() => {
    jobsNotifier.Connect().then(function (result) {
      console.debug("Hub Jobs Connected successfully to WebSocket and is listening to JobInfoMessage!");
      if (jobsNotifier.connection)
        jobsNotifier.connection.on("JobInfoMessage", (data) => {
          switch (data.op) {
            case EventNotifierOperation.Create:
              // add row to table
              setRows(
                produce((draft) => {
                  draft.push(data.data);
                })
              );
              break;
            case EventNotifierOperation.Update:
              // update row in table
              setRows(
                produce((draft) => {
                  const index = draft.findIndex((x) => x.id === data.data.id);
                  if (index !== -1) {
                    draft[index].status = data.data.status;
                    draft[index].priority = data.data.priority;
                    draft[index].progress = data.data.progress;
                    draft[index].startedAt = data.data.startedAt;
                    draft[index].endedAt = data.data.endedAt;
                    draft[index].updatedAt = data.data.updatedAt;
                    draft[index].logs = data.data.logs;
                  }
                })
              );
              break;
            case EventNotifierOperation.Delete:
              // delete row from table
              setRows(
                produce((draft) => {
                  const index = draft.findIndex((x) => x.id === data.data.id);
                  if (index !== -1) draft.splice(index, 1);
                })
              );
              break;
          }
        });
    });
    // deconnexion from HUB
    return function cleanup() {
      jobsNotifier.Disconnect().then(function (result) {});
    };
  }, []);

  const openDetailsModal = (job: DbJobInfos) => {
    if (!openModal) return;

    openModal<ModalJobDetailsElementProps>(ModalJobDetails, {
      job: job,
    });
  };

  const handleSetPriority = (way: number, id: number, priority: number) => {
    let currentPriority = priority;
    console.debug("currentPriority = " + currentPriority);

    let priorities = [10, 25, 50, 100];
    let currentPos = priorities.indexOf(currentPriority);
    let newPriorityPos = way < 0 ? currentPos - 1 : currentPos + 1;

    if (newPriorityPos < 0) newPriorityPos = 0;
    if (newPriorityPos >= priorities.length) newPriorityPos = priorities.length - 1;

    console.debug("newPriorityPos = " + newPriorityPos);
    let newPriorityValue = priorities[newPriorityPos];
    console.debug("newPriorityValue = " + newPriorityValue);

    jobBrokerService.SetPriorityOnJobItemById(id, newPriorityValue).then(function (response) {
      if (response.success) {
        enqueueCustomSnackbar(t("alert:success.itemProductPriorityWasSet"), "success");
      }
    });
  };

  const handleCancel = (id: number) => {
    isCockpit
      ? cockpitService
          .MakeAdminOperation(
            new MakeOperationRequest({
              resourceId: id,
              resourceType: ResourceType.Job,
              action: Action.Cancel,
            })
          )
          .then((response) => {
            if (response.success) enqueueCustomSnackbar(t("alert:success.jobHasBeenCancelled"), "success");
          })
      : jobBrokerService.Cancel(id).then(function (response) {
          if (response.success) enqueueCustomSnackbar(t("alert:success.jobHasBeenCancelled"), "success");
        });
  };

  const handleRetry = (id: number) => {
    if (!openModal) return;
    openModal<ModalJobRetryElementProps>(ModalJobRetry, {
      message: t("modal:message.modalJobRetryMessage"),
      title: t("modal:title.retryNow"),
      jobId: id,
      isCockpit: isCockpit,
    });
  };

  React.useEffect(() => {
    if (!jobs) return;
    setRows(jobs);
  }, [jobs]);

  const columns: GridColDef<DbJobInfos>[] = React.useMemo(
    () => [
      {
        field: "platformId",
        headerName: t("common:platform"),
        flex: CellSize.small,
        minWidth: CellSize.small,
        renderCell: (data) => {
          const platform = platforms?.find((x) => x.id === data.value);
          return platform?.name ?? "-";
        },
      },
      {
        field: "id",
        headerName: "Id",
        flex: CellSize.small,
        minWidth: CellSize.small,
        renderCell: (data) => (
          <Link underline="hover" color="primary.text" component="button" onClick={() => openDetailsModal(data.row)}>
            {data.value}
          </Link>
        ),
      },
      {
        field: "status",
        headerName: t("common:status"),
        flex: CellSize.small,
        minWidth: CellSize.small,
        type: "singleSelect",
        valueOptions: GetEnumValueLabelMap(JobStatus),
        renderCell: (data) => <TableCellJobStatus data={data} />,
      },
      {
        field: "progress",
        headerName: t("common:progress"),
        flex: CellSize.medium,
        minWidth: CellSize.medium,
        renderCell: (data) => <TableCellIngestProgressBar data={data} />,
      },
      {
        field: "type",
        headerName: t("common:type"),
        flex: CellSize.large,
        minWidth: CellSize.large,
        type: "singleSelect",
        valueOptions: GetEnumValueLabelMap(JobTypes),
        renderCell: (data) => (
          <Stack mt={0.5} mb={0.5}>
            <Typography title={data.value} variant="body2" sx={{ wordWrap: "normal", wordBreak: "break-all" }}>
              {data.value || "-"}
            </Typography>
            <Typography title={data.value} variant="body2" color="text.tier">
              {data?.row?.category || "-"}
            </Typography>
          </Stack>
        ),
      },
      /*{
                field: "syncMode",
                headerName: "SyncMode",
                flex: 50,
                minWidth: 50,
                renderCell: (data) => (
                  <Typography title={data.value} variant="body2">
                    {data.value || "-"}
                  </Typography>
                ),
              },*/
      {
        field: "priority",
        headerName: t("common:priority"),
        flex: CellSize.xsmall,
        minWidth: CellSize.xsmall,
        renderCell: (data) => (
          <TableCellJobPriority
            priority={data.value}
            onChange={(way) => handleSetPriority(way, data?.row?.id, data.value)}
          />
        ),
      },
      {
        field: "ruleName",
        headerName: t("table:headerName.ruleName"),
        flex: CellSize.xsmall,
        minWidth: CellSize.xsmall,
        renderCell: (data) => (
          <Typography title={data.value} variant="body2">
            {data.value || "-"}
          </Typography>
        ),
      },
      {
        field: "createdAt",
        headerName: t("common:creationDate"),
        flex: CellSize.small,
        minWidth: CellSize.small,
        type: "date",
        valueGetter: (data, row) => (row.createdAt ? new Date(row.createdAt) : null),
        renderCell: (data) => <TableCellDateTime data={{ value: { date: data.value } }} />,
      },
      {
        field: "startedAt",
        headerName: t("common:startDate"),
        flex: CellSize.small,
        minWidth: CellSize.small,
        type: "date",
        valueGetter: (data, row) => (row.startedAt ? new Date(row.startedAt) : null),
        renderCell: (data) => <TableCellDateTime data={{ value: { date: data.value } }} />,
      },
      {
        field: "endedAt",
        headerName: t("common:endDate"),
        flex: CellSize.small,
        minWidth: CellSize.small,
        type: "date",
        valueGetter: (data, row) => (row.endedAt ? new Date(row.endedAt) : null),
        renderCell: (data) => <TableCellDateTime data={{ value: { date: data.value } }} />,
      },
      /*{
                field: "objectId",
                headerName: "Object Id",
                flex: 100,
                minWidth: 100,
                renderCell: (data) => (
                  <Typography title={data.value} variant="body2">
                    {data.value || "-"}
                  </Typography>
                ),
              },*/
      {
        field: "objectName",
        headerName: t("common:object"),
        flex: CellSize.xlarge,
        minWidth: CellSize.xlarge,
        valueGetter: (data, row) => `${row.objectId ?? "-"} ${row.fileName}`,
        renderCell: (data) => (
          <Stack>
            <Link
              color="primary.text"
              underline="hover"
              component={RouterLink}
              to={
                data?.row?.objectLayer === "Title"
                  ? `${routePaths.title.path}/${data?.row?.objectId}`
                  : data?.row?.objectLayer === "Content"
                  ? `${routePaths.asset.path}/${data?.row?.objectId}`
                  : data?.row?.objectLayer === "Asset"
                  ? `${routePaths.asset.path}/${data?.row?.objectId}`
                  : "#"
              }
              target="_blank"
            >
              {data?.row?.objectId ?? "-"}
            </Link>
            <Typography variant="body2" color="text.tier">
              {data?.row?.fileName}
            </Typography>
          </Stack>
        ),
      },
      /*{
                field: "fileName",
                headerName: "File Name",
                flex: 220,
                minWidth: 220,
                renderCell: (data) => (
                  <Typography title={data.value} variant="body2" pt={0.5} pb={0.5}>
                    {data.value || "-"}
                  </Typography>
                ),
              },*/
      {
        field: "orderItemId",
        headerName: t("menu:title.orderId"),
        flex: CellSize.xsmall,
        minWidth: CellSize.xsmall,
        renderCell: (data) => (
          <Link
            color="primary.text"
            underline="hover"
            component={RouterLink}
            to=""
            onClick={(e) => {
              e.preventDefault();
              setPanelOrderItemOpen(true);
              setSelectedOrderItemId(data.value);
            }}
            target="_blank"
          >
            {data.value ? data.value : "-"}
          </Link>
        ),
      },
      {
        field: "comment",
        headerName: t("common:comment"),
        flex: CellSize.xsmall,
        minWidth: CellSize.xsmall,
        renderCell: (data) => (
          <Typography title={data.value} variant="body2" pt={0.5} pb={0.5}>
            {data.value ? data.value : "-"}
          </Typography>
        ),
      },
      {
        headerName: "",
        field: "actions",
        type: "actions",
        align: "right",
        flex: CellSize.actionsDefault,
        minWidth: CellSize.actionsDefault,
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            icon={
              params?.row?.status !== JobStatus.CANCELLED ? (
                [JobStatus.FAILED, JobStatus.COMPLETED, JobStatus.ABORTED].includes(params?.row?.status) ? (
                  <ReplayOutlinedIcon />
                ) : (
                  <ClearOutlinedIcon />
                )
              ) : (
                <></>
              )
            }
            title={
              params?.row?.status !== JobStatus.CANCELLED
                ? [JobStatus.FAILED, JobStatus.COMPLETED, JobStatus.ABORTED].includes(params?.row?.status)
                  ? t("common:retry")
                  : t("common:cancel")
                : ""
            }
            label={t("table:cellLabel.jobActions")}
            onClick={() =>
              params?.row?.status !== JobStatus.CANCELLED ? (
                [JobStatus.FAILED, JobStatus.COMPLETED, JobStatus.ABORTED].includes(params?.row?.status) ? (
                  handleRetry(params?.row?.id)
                ) : (
                  handleCancel(params?.row?.id)
                )
              ) : (
                <></>
              )
            }
          />,
        ],
      },
    ],
    []
  );
  // #endregion

  const filteredRows = React.useMemo(() => {
    const output = rows.filter(
      (x) =>
        (filterType === "" || x.type === filterType) &&
        (filterPlatform === -1 || x.platformId === filterPlatform) &&
        (filterStatus === undefined || x.status === filterStatus) &&
        (filterPriority === -1 || x.priority === filterPriority)
    );
    onFilteredItemCountChange && onFilteredItemCountChange(output.length);
    return output;
  }, [rows, filterType, filterStatus, filterPlatform, filterPriority]);

  const Table = React.useMemo(() => {
    return;
  }, [filteredRows, isLoading]);

  const additionalFilters = (
    <>
      {isCockpit && (
        <Select
          displayEmpty
          IconComponent={ExpandMoreRoundedIcon}
          value={filterPlatform}
          inputProps={{ "aria-label": "Without label" }}
          MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
          startAdornment={<PlaceIcon />}
          onChange={(value) => handleFilterPlatform(value)}
        >
          <MenuItem value={-1}>{t("common:platform")}</MenuItem>
          {platforms!.map((platform, index) => (
            <MenuItem value={platform.id!} key={index}>
              {platform.name}
            </MenuItem>
          ))}
        </Select>
      )}
      {isCockpit && (
        <Select
          displayEmpty
          IconComponent={ExpandMoreRoundedIcon}
          value={filterPriority}
          inputProps={{ "aria-label": "Without label" }}
          MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
          startAdornment={<PriorityHighIcon />}
          onChange={(value) => handleFilterPriority(value)}
        >
          <MenuItem value={-1}>{t("common:priority")}</MenuItem>
          {[
            { value: 10, label: "Low" },
            { value: 25, label: "Medium" },
            { value: 50, label: "High" },
            { value: 100, label: "Urgent" },
          ]!.map((p, index) => (
            <MenuItem value={p.value!} key={index}>
              {p.label}
            </MenuItem>
          ))}
        </Select>
      )}
      <Select
        displayEmpty
        startAdornment={<WorkspacesOutlined />}
        IconComponent={ExpandMoreRoundedIcon}
        inputProps={{ "aria-label": "Without label" }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        value={filterType}
        onChange={(e, v) => setFilterType(e.target.value === "" ? "" : (e.target.value as JobTypes))}
      >
        <MenuItem value="" selected>
          {t("common:type")}
        </MenuItem>
        {Object.keys(JobTypes).map((type) => (
          <MenuItem key={type} value={JobTypes[type]}>
            {type}
          </MenuItem>
        ))}
      </Select>
    </>
  );

  return (
    <>
      {!isCockpit && (
        <Grid2 container spacing={2} sx={{ pb: 2, pt: 2 }}>
          <Grid2 size={2.4}>
            <PageFilter
              variant="info"
              title={rows.length}
              subtitle={t("common:total")}
              icon={<ListIcon />}
              active={filterStatus === undefined}
              onClick={() => changeStatusFilter(undefined)}
            />
          </Grid2>
          <Grid2 size={2.4}>
            <PageFilter
              variant="primary"
              title={rows.filter((x) => x.status === JobStatus.RUNNING).length}
              subtitle={t("common:running")}
              icon={<CachedIcon />}
              active={filterStatus === JobStatus.RUNNING}
              onClick={() => changeStatusFilter(JobStatus.RUNNING)}
            />
          </Grid2>
          <Grid2 size={2.4}>
            <PageFilter
              variant="success"
              title={rows.filter((x) => x.status === JobStatus.COMPLETED).length}
              subtitle={t("common:completed")}
              icon={<TaskAltOutlinedIcon />}
              active={filterStatus === JobStatus.COMPLETED}
              onClick={() => changeStatusFilter(JobStatus.COMPLETED)}
            />
          </Grid2>
          <Grid2 size={2.4}>
            <PageFilter
              variant="error"
              title={rows.filter((x) => x.status === JobStatus.FAILED).length}
              subtitle={t("common:failed")}
              icon={<CancelOutlinedIcon />}
              active={filterStatus === JobStatus.FAILED}
              onClick={() => changeStatusFilter(JobStatus.FAILED)}
            />
          </Grid2>
          <Grid2 size={2.4}>
            <PageFilter
              variant="warning"
              title={rows.filter((x) => x.status === JobStatus.ABORTED).length}
              subtitle={t("common:aborted")}
              icon={<ErrorOutlineOutlinedIcon />}
              active={filterStatus === JobStatus.ABORTED}
              onClick={() => changeStatusFilter(JobStatus.ABORTED)}
            />
          </Grid2>
        </Grid2>
      )}

      <DataTable
        loading={isLoading}
        tableKey={TableUuids.JOBS_TABLE}
        columns={columns}
        rows={filteredRows}
        rowHeight={64}
        pagination={true}
        disableRowSelectionOnClick
        apiRef={apiGrid}
        getRowHeight={() => "auto"}
        initialColumnVisibility={{
          platformId: platforms && platforms.length > 0 ? true : false,
        }}
        onRowClick={(data) => openDetailsModal(data.row)}
        additionalFilters={additionalFilters}
      />

      <OrderItemPanel
        open={panelOrderItemOpen}
        onClose={() => setPanelOrderItemOpen(false)}
        itemId={selectedOrderItemId}
      />
    </>
  );
};

export default JobsTable;
